import { useCallback, useMemo, useState } from 'react';

import { Select } from '~/components/Select';
import { useSearchFormContext } from '~/components/Welcome/useSearchFormContext';
import { AWS_LOCATION_RESULT_TYPE, useGetAddressDetails, usePredictions, useRouter, useTranslation } from '~/hooks';

type AddressSelectProps = {
  fontSize?: 'l' | 'm';
};

export const AddressSelect = ({ fontSize }: AddressSelectProps) => {
  const { t } = useTranslation();
  const { iframe } = useRouter();
  const [search, setSearch] = useState<string | null>(null);

  const {
    form: {
      address: { value, onChange, onValidityChange },
    },
  } = useSearchFormContext();

  const getDetails = useGetAddressDetails();
  const [predictions, , getPredictions] = usePredictions({ type: AWS_LOCATION_RESULT_TYPE.CITY });

  const handleChange = useCallback(
    async (newValue: string | null) => {
      const id = predictions?.find(({ address }) => address === newValue)?.id;
      if (!id) return;

      const addressDetails = await getDetails(id);
      onChange(addressDetails);
    },
    [getPredictions, getDetails, onChange, predictions],
  );

  const handleSearchChange = useCallback(
    async (newSearch: string | null) => {
      setSearch(newSearch);
      if (!newSearch) return;
      await getPredictions(newSearch);
    },
    [getPredictions],
  );

  const predictionsOptions = useMemo(
    () => predictions?.map(({ address }) => ({ value: address })) || [],
    [predictions],
  );

  return (
    <Select
      placeholder={t('form_placeholders.address')}
      required={!iframe}
      search={search}
      fontSize={fontSize}
      onChange={handleChange}
      value={value?.name || ''}
      options={predictionsOptions}
      onSearchChange={handleSearchChange}
      onValidityChange={onValidityChange}
    />
  );
};
